import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  MenuItem,
  useTheme,
  useMediaQuery,
  Grid,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { PhoneNumberField, matchIsValidTel } from '../components/inputs/PhoneNumberField';
import ImportClientsCsvDialog from '../components/inputs/ImportClientsCsvDialog';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../constants/roles';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import SelectField from '../components/inputs/SelectField';
import LanguageSelector from '../components/inputs/LanguageSelector';
import { useAsyncAction } from '../hooks/useAsyncAction';
import { useFeedback } from '../context/FeedbackContext';
import ClientTagsRow from '../components/clients/ClientTagsRow';
import ClientTagChip from '../components/clients/ClientTagChip';
import useCompanyClientTagDefinitions from '../hooks/useCompanyClientTagDefinitions';
import { getClientTabPath } from '../utils/navigationReturn';

const Clients = () => {
  const { userRole, customPermissions } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showError, showSuccess } = useFeedback();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [clientUsers, setClientUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [showOpenChecksOnly, setShowOpenChecksOnly] = useState(false);
  const [tagFilterIds, setTagFilterIds] = useState([]);

  const { tags: companyTagDefinitions } = useCompanyClientTagDefinitions();

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', vatId: '', companyName: '', city: '', country: '', address: '', language: 'en',
  });
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const { loading: saveLoading, run: runSave } = useAsyncAction();

  const canManage = userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('manage_clients'));

  const fetchClientUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { role: ROLES.CLIENT };
      if (tagFilterIds.length > 0) {
        params.tagIds = tagFilterIds.join(',');
      }
      const response = await axios.get('/api/users', { params });
      setClientUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch client users:', error);
    } finally {
      setLoading(false);
    }
  }, [tagFilterIds]);

  useEffect(() => {
    if (canManage) fetchClientUsers();
  }, [canManage, fetchClientUsers]);

  const filteredClients = useMemo(() => {
    let list = clientUsers;

    if (statusFilter === 'active') {
      list = list.filter((u) => !u.disabled);
    } else if (statusFilter === 'disabled') {
      list = list.filter((u) => u.disabled);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((u) => {
        const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        return (
          name.includes(q) ||
          (u.email || '').toLowerCase().includes(q) ||
          (u.phone || '').toLowerCase().includes(q) ||
          (u.vatId || '').toLowerCase().includes(q) ||
          (u.companyName || '').toLowerCase().includes(q) ||
          (u.city || '').toLowerCase().includes(q) ||
          (u.country || '').toLowerCase().includes(q) ||
          (u.address || '').toLowerCase().includes(q)
        );
      });
    }

    if (showUnassignedOnly) {
      list = list.filter((u) => (u.unassignedItemsCount || 0) > 0);
    }

    if (showOpenChecksOnly) {
      list = list.filter((u) => (u.openChecksCount || 0) > 0);
    }

    return list;
  }, [clientUsers, searchQuery, statusFilter, showUnassignedOnly, showOpenChecksOnly]);

  const handleAddClient = () => {
    setSelectedUser(null);
    setUserFormData({
      firstName: '', lastName: '', email: '', phone: '', vatId: '', companyName: '', city: '', country: '', address: '', language: 'en',
    });
    setUserDialogOpen(true);
  };

  const handleSaveClient = async () => {
    const phoneTrim = (userFormData.phone || '').trim();
    if (phoneTrim && !matchIsValidTel(phoneTrim)) {
      showError(t('clients.invalidPhone'));
      return;
    }
    await runSave(async () => {
      try {
        const phonePayload = phoneTrim || undefined;
        if (selectedUser) {
          const payload = {
            firstName: userFormData.firstName,
            lastName: userFormData.lastName,
            email: userFormData.email,
            phone: phonePayload,
            vatId: userFormData.vatId,
            companyName: userFormData.companyName,
            city: userFormData.city,
            country: userFormData.country,
            address: userFormData.address,
            language: userFormData.language,
          };
          await axios.put(`/api/users/${selectedUser._id}`, payload);
        } else {
          await axios.post('/api/users/client', {
            firstName: userFormData.firstName,
            lastName: userFormData.lastName,
            email: userFormData.email,
            phone: phonePayload,
            vatId: userFormData.vatId,
            companyName: userFormData.companyName,
            city: userFormData.city,
            country: userFormData.country,
            address: userFormData.address,
            language: userFormData.language,
          });
          showSuccess(t('clients.clientCreated'));
        }
        setUserDialogOpen(false);
        fetchClientUsers();
      } catch (error) {
        showError(error.response?.data?.error || 'Failed to save client');
      }
    });
  };

  const getClientName = (user) => `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '—';

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <Typography variant="h4">{t('clients.title')}</Typography>
          {canManage && (
            <Box display="flex" gap={1}>
              <Button variant="outlined" onClick={() => setCsvImportOpen(true)} startIcon={<AddIcon />}>
                {t('clients.importCsv')}
              </Button>
              <Button variant="contained" onClick={handleAddClient} startIcon={<AddIcon />}>
                {t('clients.addClient')}
              </Button>
            </Box>
          )}
        </Box>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              size="small"
              placeholder={t('clients.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: isMobile ? '100%' : 280, flex: isMobile ? 1 : undefined }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <SelectField
              label={t('common.status')}
              value={statusFilter}
              onChange={(next) => setStatusFilter(next)}
              options={[
                { value: 'all', label: t('common.all') },
                { value: 'active', label: t('status.active') },
                { value: 'disabled', label: t('status.disabled') },
              ]}
              emptyLabel=""
              sx={{ minWidth: 140 }}
            />
            <FormControlLabel
              control={<Checkbox checked={showUnassignedOnly} onChange={(e) => setShowUnassignedOnly(e.target.checked)} size="small" />}
              label={<Typography variant="body2">{t('clients.unassignedItems')}</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={showOpenChecksOnly} onChange={(e) => setShowOpenChecksOnly(e.target.checked)} size="small" />}
              label={<Typography variant="body2">{t('clients.openChecks')}</Typography>}
            />
            {companyTagDefinitions.length > 0 && (
              <SelectField
                label={t('clients.tags.filterLabel')}
                value={tagFilterIds}
                onChange={(next) => setTagFilterIds(Array.isArray(next) ? next.map(String) : [])}
                multiple
                options={companyTagDefinitions.map((tag) => ({
                  value: String(tag._id),
                  label: tag.name,
                }))}
                emptyLabel={t('clients.tags.filterAll')}
                sx={{ minWidth: isMobile ? '100%' : 200, maxWidth: isMobile ? '100%' : 320 }}
                renderValue={(selected) => {
                  const ids = Array.isArray(selected) ? selected : [];
                  if (ids.length === 0) return t('clients.tags.filterAll');
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.25 }}>
                      {ids.map((id) => {
                        const tag = companyTagDefinitions.find((x) => String(x._id) === String(id));
                        if (!tag) return null;
                        return <ClientTagChip key={id} tag={tag} />;
                      })}
                    </Box>
                  );
                }}
              >
                {companyTagDefinitions.map((tag) => (
                  <MenuItem key={tag._id} value={String(tag._id)}>
                    <ClientTagChip tag={tag} />
                  </MenuItem>
                ))}
              </SelectField>
            )}
          </Box>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          /* ── Mobile card list ── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filteredClients.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {clientUsers.length === 0 ? t('clients.noClients') : t('clients.noClientsMatch')}
              </Typography>
            ) : filteredClients.map((user) => (
              <Paper
                key={user._id}
                variant="outlined"
                sx={{ p: 2, cursor: 'pointer', opacity: user.disabled ? 0.6 : 1, '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => {
                  const ids = filteredClients.map((u) => u._id);
                  navigate(getClientTabPath(user._id, 'items'), { state: { ids, index: ids.indexOf(user._id) } });
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                      <Typography variant="subtitle2" fontWeight={600}>{getClientName(user)}</Typography>
                      <Chip
                        label={user.disabled ? t('status.disabled') : t('status.active')}
                        color={user.disabled ? 'error' : 'success'}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    {(user.clientTags || []).length > 0 && (
                      <ClientTagsRow tags={user.clientTags} maxVisible={4} sx={{ mb: 0.5 }} />
                    )}
                    {user.email && <Typography variant="body2" color="text.secondary">{user.email}</Typography>}
                    {user.phone && <Typography variant="body2" color="text.secondary">{user.phone}</Typography>}
                    {user.vatId && <Typography variant="caption" color="text.secondary">{t('clients.vatId')}: {user.vatId}</Typography>}
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5} flexShrink={0}>
                    {user.unassignedItemsCount > 0 && (
                      <Chip label={`${t('clients.unassignedItems')}: ${user.unassignedItemsCount}`} color="warning" size="small" />
                    )}
                    {user.openChecksCount > 0 && (
                      <Chip label={`${t('clients.openChecks')}: ${user.openChecksCount}`} color="info" size="small" />
                    )}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          /* ── Desktop table ── */
          <TableContainer component={Paper} sx={{ maxHeight: 'max(200px, calc(100dvh - 280px))', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('clients.name')}</TableCell>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('clients.tags.column')}</TableCell>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('clients.email')}</TableCell>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('clients.phone')}</TableCell>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('clients.vatId')}</TableCell>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('clients.unassignedItems')}</TableCell>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('clients.openChecks')}</TableCell>
                  <TableCell sx={{ backgroundColor: 'background.paper' }}>{t('common.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClients.map((user) => (
                  <TableRow
                    key={user._id}
                    hover
                    sx={{ cursor: 'pointer', ...(user.disabled ? { opacity: 0.5 } : {}) }}
                    onClick={() => {
                      const ids = filteredClients.map((u) => u._id);
                      navigate(getClientTabPath(user._id, 'items'), { state: { ids, index: ids.indexOf(user._id) } });
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {getClientName(user)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      {(user.clientTags || []).length > 0 ? (
                        <ClientTagsRow tags={user.clientTags} maxVisible={3} />
                      ) : t('common.emptyValue')}
                    </TableCell>
                    <TableCell>{user.email || '—'}</TableCell>
                    <TableCell>{user.phone || '—'}</TableCell>
                    <TableCell>{user.vatId || '—'}</TableCell>
                    <TableCell>
                      {user.unassignedItemsCount > 0 ? (
                        <Chip label={user.unassignedItemsCount} color="warning" size="small" />
                      ) : '0'}
                    </TableCell>
                    <TableCell>
                      {user.openChecksCount > 0 ? (
                        <Chip label={user.openChecksCount} color="info" size="small" />
                      ) : '0'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.disabled ? t('status.disabled') : t('status.active')}
                        color={user.disabled ? 'error' : 'success'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {filteredClients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {clientUsers.length === 0 ? t('clients.noClients') : t('clients.noClientsMatch')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ──── Import Clients CSV Dialog ──── */}
        <ImportClientsCsvDialog
          open={csvImportOpen}
          onClose={() => setCsvImportOpen(false)}
          onImported={fetchClientUsers}
        />

        {/* ──── Add/Edit Client Dialog ──── */}
        <Dialog
          open={userDialogOpen}
          onClose={() => setUserDialogOpen(false)}
          maxWidth="md"
          fullWidth
          scroll="paper"
          PaperProps={{
            sx: {
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90dvh',
            },
          }}
        >
          <DialogTitle sx={{ px: 3, pt: 2.5, pb: 2, flexShrink: 0 }}>
            {selectedUser ? t('clients.editClient') : t('clients.addClient')}
          </DialogTitle>
          <DialogContent dividers sx={{ px: 3, pt: 3, pb: 3, flex: '1 1 auto', overflowY: 'auto' }}>
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.firstName')} value={userFormData.firstName}
                  onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value })}
                  margin="none" required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.lastName')} value={userFormData.lastName}
                  onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value })}
                  margin="none" required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.email')} type="email" value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  margin="none" required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PhoneNumberField
                  label={t('clients.phoneOptional')}
                  value={userFormData.phone}
                  onChange={(value) => setUserFormData({ ...userFormData, phone: value })}
                  helperText={t('clients.phoneHelper')}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.vatId')} value={userFormData.vatId}
                  onChange={(e) => setUserFormData({ ...userFormData, vatId: e.target.value })}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.companyName')} value={userFormData.companyName}
                  onChange={(e) => setUserFormData({ ...userFormData, companyName: e.target.value })}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.city')} value={userFormData.city}
                  onChange={(e) => setUserFormData({ ...userFormData, city: e.target.value })}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label={t('clients.country')} value={userFormData.country}
                  onChange={(e) => setUserFormData({ ...userFormData, country: e.target.value })}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label={t('clients.address')} value={userFormData.address}
                  onChange={(e) => setUserFormData({ ...userFormData, address: e.target.value })}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ pt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    {t('clients.language')}
                  </Typography>
                  <LanguageSelector
                    value={userFormData.language}
                    onChange={(value) => setUserFormData({ ...userFormData, language: value })}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    {t('clients.languageHelp')}
                  </Typography>
                </Box>
              </Grid>
              {selectedUser?.invoiceExternalId && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth label={t('clients.invoiceExternalId')} value={selectedUser.invoiceExternalId}
                    margin="none"
                    disabled
                    helperText={t('clients.invoiceExternalIdHelper')}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ flexShrink: 0, px: 3, py: 2 }}>
            <Button onClick={() => setUserDialogOpen(false)} disabled={saveLoading}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveClient} variant="contained" disabled={saveLoading}>
              {saveLoading
                ? (selectedUser ? t('common.saving') : t('common.creating'))
                : (selectedUser ? t('common.save') : t('clients.createClient'))}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default Clients;
